/**
 * Deal Room uploads straight from the browser to storage.
 *
 * The multipart engine (`uploadEngine.ts`) sends every byte through the
 * backend, and Cloud Run refuses any request over 32 MiB — a single financial
 * model can be bigger. Here each file goes to Cloud Storage over a resumable
 * session the backend opens for it:
 *
 *   1. `POST /files/uploads`          — the backend checks the deal, the path,
 *                                        the type and the size, and returns a
 *                                        session URL plus a signed ticket;
 *   2. `PUT <session URL>`, in chunks — straight to storage; a failed chunk is
 *                                        retried after asking storage how much
 *                                        it already has, so nothing re-sends
 *                                        from zero;
 *   3. `POST /files/uploads/complete` — the backend verifies what landed and
 *                                        records it in the deal;
 *   4. `POST /files/uploads/finish`   — once, after every file succeeded: the
 *                                        deal's ingest (the multipart path's
 *                                        `is_last_batch`).
 *
 * Files go up DIRECT_UPLOAD_CONCURRENCY at a time and fail independently.
 * Zips (unpacked server-side), empty files, and any file the browser cannot
 * reach storage for (a corporate network blocking it) fall back to the
 * multipart engine — which can still take anything under its request limit.
 */

import { apiService } from "../services/api";
import { apiFetch } from "./apiClient";
import { relativePathFor } from "./folderUpload";
import {
	FILE_NOT_SENT_ERROR,
	isReadable,
	runUploadJob,
	UNREADABLE_FILE_ERROR,
	UploadJobRequest,
	UploadResponse,
} from "./uploadEngine";

/** Files uploading at once. More only splits the same bandwidth thinner. */
export const DIRECT_UPLOAD_CONCURRENCY = 3;
/** Attempts per chunk before the file is given up on. */
const CHUNK_ATTEMPTS = 5;
/** The multipart path's practical ceiling (Cloud Run's 32 MiB, minus form
 *  overhead). A file above it has nowhere to fall back to. */
export const MULTIPART_FALLBACK_MAX_BYTES = 30 * 1024 * 1024;

export const STORAGE_UNREACHABLE_ERROR =
	"Your network blocked the upload to secure storage. Ask IT to allow storage.googleapis.com, or upload from another network.";

type UploadedFile = UploadResponse["uploaded_files"][number];

interface StartedUpload {
	ticket: string;
	upload_url: string;
	chunk_size: number;
}

/** A failure the user should read as-is. */
class DirectUploadError extends Error {
	constructor(
		message: string,
		/** Nothing reached storage, so the multipart path may still work. */
		readonly mayFallBack = false
	) {
		super(message);
	}
}

const v3 = () => apiService.getBaseUrlForVersion("v3");

const detailOf = async (res: Response, fallback: string): Promise<string> => {
	try {
		const body = await res.json();
		if (typeof body?.detail === "string") return body.detail;
	} catch {
		// not JSON — fall through
	}
	return fallback;
};

const postJson = async <T>(path: string, body: unknown): Promise<T> => {
	let res: Response;
	try {
		res = await apiFetch(`${v3()}${path}`, {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify(body),
		});
	} catch {
		throw new DirectUploadError(FILE_NOT_SENT_ERROR, true);
	}
	if (!res.ok) {
		throw new DirectUploadError(
			await detailOf(res, `Upload failed (${res.status}).`),
			res.status >= 500
		);
	}
	return (await res.json()) as T;
};

interface ChunkResult {
	status: number;
	/** Storage's `Range` header, when CORS lets the browser read it. */
	range: string | null;
}

/** One PUT to the session. Rejects only when no response came back at all. */
const putToSession = (
	url: string,
	body: Blob | null,
	contentRange: string,
	onSent?: (bytes: number) => void
): Promise<ChunkResult> =>
	new Promise((resolve, reject) => {
		const xhr = new XMLHttpRequest();
		if (onSent) {
			xhr.upload.addEventListener("progress", (e: ProgressEvent) => {
				if (e.lengthComputable) onSent(e.loaded);
			});
		}
		xhr.addEventListener("load", () =>
			resolve({ status: xhr.status, range: xhr.getResponseHeader("Range") })
		);
		xhr.addEventListener("error", () => reject(new Error("network")));
		xhr.addEventListener("abort", () => reject(new Error("aborted")));
		xhr.open("PUT", url);
		// The session URL is the credential; no Authorization header goes to
		// storage.
		xhr.setRequestHeader("Content-Range", contentRange);
		xhr.send(body);
	});

/** Next byte storage wants, from a `Range: bytes=0-N` header. */
const nextOffsetFrom = (range: string | null): number | null => {
	const match = range?.match(/bytes=0-(\d+)/);
	return match ? Number(match[1]) + 1 : null;
};

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/** Tries at `/files/uploads/finish`, and the base wait between them. */
const FINISH_ATTEMPTS = 3;
const FINISH_RETRY_MS = 1000;

/**
 * Send the file's bytes to its session, chunk by chunk. Resolves once storage
 * has the whole file.
 */
const sendToStorage = async (
	file: File,
	started: StartedUpload,
	onBytes: (confirmed: number, inFlight: number) => void
): Promise<void> => {
	const total = file.size;
	let offset = 0;
	let everAccepted = false;
	while (offset < total) {
		const end = Math.min(offset + started.chunk_size, total);
		let sent = false;
		for (let attempt = 1; attempt <= CHUNK_ATTEMPTS && !sent; attempt++) {
			let result: ChunkResult;
			const chunkStart = offset;
			try {
				result = await putToSession(
					started.upload_url,
					file.slice(chunkStart, end),
					`bytes ${chunkStart}-${end - 1}/${total}`,
					(loaded) => onBytes(chunkStart, loaded)
				);
			} catch {
				// No response at all. On the very first chunk that usually means
				// storage is unreachable from this network, not a blip.
				if (!everAccepted && attempt === 2) {
					throw new DirectUploadError(STORAGE_UNREACHABLE_ERROR, true);
				}
				await sleep(500 * 2 ** attempt);
				offset = (await askStorageForOffset(started.upload_url, total)) ?? offset;
				// Storage may already hold this chunk (the reply was what got lost).
				if (offset >= end) sent = true;
				continue;
			}
			if (result.status === 200 || result.status === 201) {
				onBytes(total, 0);
				return;
			}
			if (result.status === 308) {
				everAccepted = true;
				// Storage may keep less than it was sent; resume where it says.
				offset = nextOffsetFrom(result.range) ?? end;
				onBytes(offset, 0);
				sent = true;
				continue;
			}
			if (result.status === 429 || result.status >= 500) {
				await sleep(500 * 2 ** attempt);
				offset = (await askStorageForOffset(started.upload_url, total)) ?? offset;
				// Storage may already hold this chunk (the reply was what got lost).
				if (offset >= end) sent = true;
				continue;
			}
			// 4xx: the session is gone or the request is wrong; retrying won't help.
			throw new DirectUploadError(
				`Storage refused the upload (${result.status}). Please try again.`
			);
		}
		if (!sent) throw new DirectUploadError(FILE_NOT_SENT_ERROR);
	}
};

/** How many bytes storage already has, or null if it can't be asked. */
const askStorageForOffset = async (
	url: string,
	total: number
): Promise<number | null> => {
	try {
		const res = await putToSession(url, null, `bytes */${total}`);
		if (res.status === 200 || res.status === 201) return total;
		if (res.status === 308) return nextOffsetFrom(res.range) ?? 0;
	} catch {
		// offline — keep the current offset and let the retry try again
	}
	return null;
};

/** One file, start to recorded. Throws DirectUploadError. */
const uploadOneFile = async (
	file: File,
	projectId: string,
	onBytes: (confirmed: number, inFlight: number) => void
): Promise<UploadedFile> => {
	const started = await postJson<StartedUpload>("/files/uploads", {
		project_id: projectId,
		relative_path: relativePathFor(file),
		size: file.size,
		content_type: file.type || null,
	});
	await sendToStorage(file, started, onBytes);
	const done = await postJson<{ uploaded_file: UploadedFile }>(
		"/files/uploads/complete",
		{ ticket: started.ticket, overwrite_existing: true }
	);
	return done.uploaded_file;
};

/** Runs `worker` over `items`, at most `limit` at a time, in input order. */
const mapLimited = async <T, R>(
	items: T[],
	limit: number,
	worker: (item: T, index: number) => Promise<R>
): Promise<R[]> => {
	const results: R[] = new Array(items.length);
	let next = 0;
	const lanes = Array.from({ length: Math.min(limit, items.length) }, async () => {
		while (next < items.length) {
			const index = next++;
			results[index] = await worker(items[index], index);
		}
	});
	await Promise.all(lanes);
	return results;
};

const isZip = (file: File) => file.name.toLowerCase().endsWith(".zip");

/**
 * Run one upload job over the direct path. Same contract as `runUploadJob`:
 * never rejects for a per-file problem; failures come back as `errors` and an
 * `aborted` result, and the deal's ingest is queued only when every file made
 * it.
 */
export const runDirectUploadJob = async (
	request: UploadJobRequest
): Promise<UploadResponse> => {
	const uploaded: UploadedFile[] = [];
	const errors: UploadResponse["errors"] = [];

	const readability = await Promise.all(request.files.map(isReadable));
	request.files.forEach((file, i) => {
		if (!readability[i]) {
			errors.push({ filename: relativePathFor(file), error: UNREADABLE_FILE_ERROR });
		}
	});
	const readable = request.files.filter((_, i) => readability[i]);
	// Zips unpack server-side and empty files have nothing to stream: both keep
	// the multipart path.
	const viaMultipart = readable.filter((f) => isZip(f) || f.size === 0);
	const direct = readable.filter((f) => !isZip(f) && f.size > 0);

	// Progress by bytes across every direct file.
	const totalBytes = direct.reduce((sum, f) => sum + f.size, 0) || 1;
	const confirmed = new Array(direct.length).fill(0);
	const inFlight = new Array(direct.length).fill(0);
	const report = () => {
		const sent = confirmed.reduce((a, b) => a + b, 0) + inFlight.reduce((a, b) => a + b, 0);
		request.onProgress?.(Math.min(99, Math.round((sent / totalBytes) * 100)));
	};

	await mapLimited(direct, DIRECT_UPLOAD_CONCURRENCY, async (file, i) => {
		try {
			uploaded.push(
				await uploadOneFile(file, request.projectId, (done, sending) => {
					confirmed[i] = done;
					inFlight[i] = sending;
					report();
				})
			);
		} catch (error) {
			const directError =
				error instanceof DirectUploadError
					? error
					: new DirectUploadError(FILE_NOT_SENT_ERROR);
			// Nothing reached storage and the file fits the old path: try it there.
			if (directError.mayFallBack && file.size <= MULTIPART_FALLBACK_MAX_BYTES) {
				viaMultipart.push(file);
			} else {
				errors.push({ filename: relativePathFor(file), error: directError.message });
			}
		}
	});

	if (viaMultipart.length > 0) {
		const fallback = await runUploadJob({
			...request,
			files: viaMultipart,
			onProgress: undefined,
			// The ingest is queued below, once, for the whole upload.
			finalize: false,
		});
		uploaded.push(...fallback.uploaded_files);
		errors.push(...fallback.errors);
		fallback.aborted?.notAttempted.forEach((filename) =>
			errors.push({ filename, error: FILE_NOT_SENT_ERROR })
		);
	}

	if (errors.length === 0) {
		// Every file is stored and recorded; the finish queues the deal's ingest.
		// Retry a transient failure — a lost ingest is otherwise only caught up
		// by the next upload to this deal (it re-derives the whole room).
		for (let attempt = 1; attempt <= FINISH_ATTEMPTS; attempt++) {
			try {
				await postJson("/files/uploads/finish", { project_id: request.projectId });
				break;
			} catch (error) {
				if (attempt === FINISH_ATTEMPTS) {
					console.warn("Upload finish failed; the deal's ingest was not queued", error);
				} else {
					await sleep(FINISH_RETRY_MS * attempt);
				}
			}
		}
	}
	request.onProgress?.(100);

	return {
		uploaded_files: uploaded,
		total_uploaded: uploaded.length,
		errors,
		total_errors: errors.length,
		project_id: request.projectId,
		aborted:
			errors.length === 0
				? undefined
				: {
						uploadedBeforeFailure: uploaded.length,
						failed: errors.map((e) => e.filename),
						notAttempted: [],
					},
	};
};
